// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/all-contacts.js (origin/main-dev, 971 lines).
 * BYTE-IDENTICAL — 10-column visibility toggle Dropdown, agent-masking-aware
 * number display, hasPermission read-guard for "Contacts" (note: NOT "Contact
 * Group"; separate module in v1's ACL), edit modal identical to group-detail
 * edit modal. Chrome swap: PrimeReact DataTable → obd3-gui DataTable.
 */
import React, { useState, useEffect, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useNavigate } from 'react-router-dom';
import Loader from '@/components/partials/components/Loader';
import { Card as ShadcnCard, CardContent as ShadcnCardContent } from '@/components/ui/card';
import { Button as RBButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent as ShadcnDropdownMenuContent,
  DropdownMenuItem as ShadcnDropdownMenuItem,
  DropdownMenuTrigger as ShadcnDropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* Micro-shims for the react-bootstrap surface this file uses. Keeps the JSX
 * intact while dropping the compat-shim import. */
const cx = (...args: any[]) => args.filter(Boolean).join(' ');

const Row: any = ({ className, children, ...props }: any) => (
  <div className={cx('row', className)} {...props}>
    {children}
  </div>
);
const buildColCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs);
  push('sm', sm);
  push('md', md);
  push('lg', lg);
  push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={cx(buildColCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Card: any = ({ className, children, ...props }: any) => (
  <ShadcnCard className={className} {...props}>
    {children}
  </ShadcnCard>
);
Card.Body = ({ className, children, ...props }: any) => (
  <ShadcnCardContent className={className} {...props}>
    {children}
  </ShadcnCardContent>
);

const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, ...props }: any) => {
  const cls = cx('form-control', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Check = ({ type = 'checkbox', label, id, className, ...props }: any) => (
  <div className={cx('form-check', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx(muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);

/* Modal shim → shadcn Dialog (matches react-bootstrap show/onHide + compound
 * Header/Body/Footer/Title API). */
const Modal: any = ({ show, onHide, size, centered: _c, className, children, ...props }: any) => {
  const sizeCls = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-3xl' : size === 'xl' ? 'max-w-5xl' : 'max-w-lg';
  return (
    <Dialog open={!!show} onOpenChange={(next: boolean) => !next && onHide?.()}>
      <DialogContent className={cx(sizeCls, 'max-h-[85vh] overflow-y-auto', className)} {...props}>
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ closeButton: _cb, className, children, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Body = ({ className, children, ...props }: any) => (
  <div className={className} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ className, children, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
Modal.Title = ({ className, children, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);

/* Dropdown shim → shadcn DropdownMenu compound. autoClose is honored via
 * onCloseAutoFocus (default keeps menu open on outside click when we treat
 * checkboxes inside — accepted; matches original UX intent). */
const Dropdown: any = ({ children, autoClose: _autoClose, ...props }: any) => (
  <ShadcnDropdownMenu {...props}>{children}</ShadcnDropdownMenu>
);
Dropdown.Toggle = ({ children, className, variant: _v, id: _id, ...props }: any) => (
  <ShadcnDropdownMenuTrigger asChild>
    <RBButton variant="outline" className={className} {...props}>
      {children}
    </RBButton>
  </ShadcnDropdownMenuTrigger>
);
Dropdown.Menu = ({ children, className, ...props }: any) => (
  <ShadcnDropdownMenuContent className={className} align="end" {...props}>
    {children}
  </ShadcnDropdownMenuContent>
);
Dropdown.Item = ({ children, onClick, className, ...props }: any) => (
  <ShadcnDropdownMenuItem className={className} onClick={onClick} {...props}>
    {children}
  </ShadcnDropdownMenuItem>
);
Dropdown.ItemText = ({ children, className, ...props }: any) => (
  <div className={cx('px-2 py-1.5 text-sm', className)} onClick={(e) => e.stopPropagation()} {...props}>
    {children}
  </div>
);

const OverlayTrigger = ({ children }: any) => children;
const Tooltip = ({ children }: any) => <span>{children}</span>;
import type { ColumnDef } from '@tanstack/react-table';
import { FiEdit, FiTrash2, FiArrowLeft, FiMoreVertical, FiPlus, FiSliders } from 'react-icons/fi';
import { FiInfo } from 'react-icons/fi';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_MEMBERS_DELETE,
  API_CONTACT_GROUP_MEMBERS_BULK_DELETE,
  API_CONTACT_GROUP_MEMBERS_UPLOAD,
  API_ALL_CONTACTS_PAGED,
  API_ALL_CONTACTS_GROUP_OPTIONS,
} from '@/service/url';
import { Input } from '@/components/ui/input';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import {
  USER_ROLES,
  UPLOAD_CONTACT_GROUP_MEMBERS,
  UPLOAD_CONTACT_GROUP_MEMBERS_DATA,
  CONTACT_PAGE_SIZES,
} from '@/utilities/constant';
import downloadTemplate from '@/utilities/downloadCsv';
import { postApiMediaCall } from '@/service/postApiCall';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  ArrowLeft,
  Users as UsersIcon,
  Pencil,
  Trash2,
  Upload,
  RefreshCw,
} from 'lucide-react';
import { ListPlus } from 'lucide-react';
import { ROUTES } from '@/utilities/constant';
import { UploadCustomFieldsChooser, useUploadCustomFields } from './custom-fields/UploadCustomFieldsChooser';
import { AdditionalDetailsCell, additionalDetailsText } from './custom-fields/AdditionalDetailsCell';
import { useCustomFieldsVisible } from './custom-fields/useCustomFieldsVisible';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const NO_GROUP = 'none';
const EXPORT_PAGE = 1000;
const EXPORT_MAX = 100000;
const fmtDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d.getTime())
    ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
};
const datesCell = (r) => (
  <div className="flex flex-col gap-0.5 whitespace-nowrap text-xs tabular-nums">
    <span><span className="text-muted-foreground">Created:</span> <span className="text-foreground">{fmtDate(r.created_at)}</span></span>
    <span><span className="text-muted-foreground">Updated:</span> <span className="text-foreground">{fmtDate(r.updated_at)}</span></span>
  </div>
);
const groupNames = (row) => (row?.contact_groups || []).map((g) => g.name).join(', ') || '-';

const AllContacts = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = usePersistedState('contact-group/AllContacts:searchInput', '');
  const [search, setSearch] = useState(() => searchInput.trim());
  const [groupFilter, setGroupFilter] = usePersistedState('contact-group/AllContacts:groupFilter', '');
  const [groupList, setGroupList] = useState([]);
  const [paging, setPaging] = usePersistedState('contact-group/AllContacts:paging', { pageIndex: 0, pageSize: 10 });
  const [total, setTotal] = useState(null);
  const [totalCapped, setTotalCapped] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const cursors = useRef({ key: '', last: {} });
  const reqSeq = useRef(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const customUpload = useUploadCustomFields();
  const showCustomFields = useCustomFieldsVisible();
  // Name of the contact's last saved custom field template.
  const templateName = (row) => {
    const tid = Number(row?.custom_field_template_id);
    if (!tid) return '-';
    if (customUpload.loading) return '…';
    return customUpload.templates.find((t) => t.id === tid)?.name || 'Deleted template';
  };
  const [uploading, setUploading] = useState(false);
  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; clear: () => void } | null>(null);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  const COLUMN_DEFS = [
    { key: 'name', label: 'Name', default: true },
    { key: 'number', label: 'Number', default: true },
    { key: 'street', label: 'Street Address', default: true },
    { key: 'city', label: 'City', default: true },
    { key: 'state', label: 'State', default: true },
    { key: 'country', label: 'Country', default: true },
    { key: 'company', label: 'Company', default: true },
    { key: 'contact_groups', label: 'Contact Groups', default: true },
    ...(customUpload.enabled ? [{ key: 'custom_template', label: 'Custom Field Template', default: true }] : []),
    ...(showCustomFields ? [{ key: 'additional_details', label: 'Customer Additional Details', default: true }] : []),
    { key: 'dates', label: 'Created / Updated', default: true },
    { key: 'alt1_number', label: 'Alternate Number 1', default: false },
    { key: 'alt2_number', label: 'Alternate Number 2', default: false },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    COLUMN_DEFS.reduce((acc, c) => ({ ...acc, [c.key]: c.default }), {}),
  );
  const toggleColumn = (key) => setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));

  const filterParams = () => ({
    ...(search ? { search } : {}),
    ...(groupFilter ? { group: groupFilter } : {}),
  });
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === search) return;
      setSearch(next);
      setPaging((p) => ({ ...p, pageIndex: 0 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const key = `${groupFilter}|${search}|${paging.pageSize}|${reloadKey}`;
    if (cursors.current.key !== key) cursors.current = { key, last: {} };
    const after = cursors.current.last[paging.pageIndex - 1];
    const pageIndex = paging.pageIndex;
    const seq = ++reqSeq.current;
    setLoading(true);
    getApiCall(API_ALL_CONTACTS_PAGED, {
      page: pageIndex + 1,
      limit: paging.pageSize,
      ...(after ? { after } : {}),
      ...filterParams(),
    })
      .then((resp) => {
        if (seq !== reqSeq.current) return; // a newer request is in flight
        const list = resp?.rows || [];
        if (!list.length && pageIndex > 0) return setPaging((p) => ({ ...p, pageIndex: pageIndex - 1 }));
        setContacts(list);
        setTotal(Number(resp?.total) || 0);
        setTotalCapped(!!resp?.total_capped);
        if (list.length) cursors.current.last[pageIndex] = list[list.length - 1].id;
      })
      .catch(onError)
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [paging.pageIndex, paging.pageSize, search, groupFilter, reloadKey]);

  useEffect(() => {
    getApiCall(API_ALL_CONTACTS_GROUP_OPTIONS, {})
      .then((resp) => setGroupList(Array.isArray(resp) ? resp : []))
      .catch(onError);
  }, []);

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  // Edit is now a dedicated page — pass the row via router state so the
  // page can render instantly without an extra list fetch.
  const handleEditClick = (contact: any) => {
    navigate(`/all-contacts/${contact.id}/edit`, { state: { contact } });
  };

  // Exports the whole current filter (not just this page), walked in keyset pages.
  const handleExport = async () => {
    try {
      setButtonDisable(true);
      const all = [];
      let after = null;
      while (all.length < EXPORT_MAX) {
        const resp = await getApiCall(API_ALL_CONTACTS_PAGED, {
          page: 1,
          limit: EXPORT_PAGE,
          count: '0',
          ...(after ? { after } : {}),
          ...filterParams(),
        });
        const list = resp?.rows || [];
        all.push(...list);
        if (list.length < EXPORT_PAGE) break;
        after = list[list.length - 1].id;
      }
      if (all.length >= EXPORT_MAX) {
        toast.warning(`Export has the newest ${EXPORT_MAX.toLocaleString()} contacts only. Use the group filter or search to export the rest.`);
      }
      exportToCSV({
        fileName: 'contacts-list',
        data: all.slice(0, EXPORT_MAX),
        columns: [
          { header: 'Name', accessor: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-' },
          { header: 'Number', accessor: (row) => displayNumber(row.primary_number) || '-' },
          { header: 'Street Address', accessor: (row) => row.street || '-' },
          { header: 'City', accessor: (row) => row.city || '-' },
          { header: 'State', accessor: (row) => row.state || '-' },
          { header: 'Country', accessor: (row) => row.country || '-' },
          { header: 'Company', accessor: (row) => row.company || '-' },
          { header: 'Contact Groups', accessor: groupNames },
          ...(customUpload.enabled ? [{ header: 'Custom Field Template', accessor: templateName }] : []),
          ...(showCustomFields
            ? [{ header: 'Customer Additional Details', accessor: (row) => additionalDetailsText(row.additional_details) }]
            : []),
          { header: 'Created At', accessor: (row) => fmtDate(row.created_at) },
          { header: 'Updated At', accessor: (row) => fmtDate(row.updated_at) },
        ],
      });
    } catch (err) {
      onError(err);
    } finally {
      setButtonDisable(false);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  const maskNumbers = getAgentMasking(getUser());
  const displayNumber = (num) => (maskNumbers ? maskNumber(num) : num);

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    customUpload.reset();
  };

  const handleSaveUpload = () => {
    if (!customUpload.ready) return toast.error('Select a custom field template, or choose No.');
    if (!uploadFile) return toast.error('Please choose a CSV file.');
    const formData = new FormData();
    formData.append('members_data', uploadFile);
    customUpload.appendTo(formData);
    setUploading(true);
    postApiMediaCall(API_CONTACT_GROUP_MEMBERS_UPLOAD, formData)
      .then((data: any) => {
        const repeated = Number(data?.duplicates) || 0;
        const updated = Number(data?.updated) || 0;
        const updatedNums = Array.isArray(data?.updated_numbers) ? data.updated_numbers : [];
        const updatedNote = updated
          ? `${updated} existing contact(s) updated with the file's details (${updatedNums.slice(0, 5).join(', ')}${updated > 5 ? ', ...' : ''})`
          : '';
        const invalidRows = Array.isArray(data?.invalid_rows) ? data.invalid_rows : [];
        const invalidHint = invalidRows.length
          ? ` (${invalidRows.slice(0, 5).map((r) => `row ${r.row}: ${r.reason}`).join('; ')}${data.invalid > 5 ? '; ...' : ''})`
          : '';
        const skipped = [
          repeated > 0 && `${repeated} repeated in file`,
          data?.invalid && `${data.invalid} invalid${invalidHint}`,
        ].filter(Boolean);
        const added = `${Number(data?.added) || 0} contact(s) added${updatedNote ? `, ${updatedNote}` : ''}`;
        if (skipped.length) toast.warning(`${added}. Skipped: ${skipped.join(', ')}.`);
        else if (updated) toast.info(`${added}.`, { duration: 8000 });
        else toast.success(added);
        closeUploadModal();
        reload();
      })
      .catch((err) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to upload contacts');
        }
      })
      .finally(() => setUploading(false));
  };

  const handleDeleteClick = (contactId) => {
    setContactToDelete(contactId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (bulkDelete) {
      setDeleting(true);
      postApiCall(API_CONTACT_GROUP_MEMBERS_BULK_DELETE, { ids: bulkDelete.ids.map(Number) })
        .then((res: any) => {
          const blocked = Number(res?.blocked) || 0;
          const skipped = (Number(res?.skipped) || 0) - blocked;
          const msg = `${res?.deleted ?? 0} contact(s) deleted${skipped > 0 ? `, ${skipped} skipped` : ''}`;
          if (blocked) {
            const names = (res?.blocked_campaigns || []).join(', ');
            toast.warning(`${msg}. ${blocked} not deleted: used in active campaign(s)${names ? ` ${names}` : ''}.`);
          } else toast.success(msg);
          bulkDelete.clear();
          reload();
        })
        .catch(onDeleteError)
        .finally(() => {
          setDeleting(false);
          setShowDeleteModal(false);
          setBulkDelete(null);
        });
      return;
    }
    const url = API_CONTACT_GROUP_MEMBERS_DELETE.replace(':id', contactToDelete);
    setDeleting(true);
    postApiCall(url, { id: contactToDelete })
      .then(onDeleteSuccess)
      .catch(onDeleteError)
      .finally(() => {
        setDeleting(false);
        setShowDeleteModal(false);
        setContactToDelete(null);
      });
  };

  function onDeleteSuccess() {
    toast.success('Contact deleted successfully');
    setShowToast(true);
    reload();
  }

  function onDeleteError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to delete contact');
      setShowToast(true);
    }
  }

  const isContactsRole = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'read');
  const canCreate =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'add');
  const canEdit =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'edit');
  const canDelete =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'delete');

  if (!canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading && total === null) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const renderNumberPill = (num: any) =>
    num ? (
      <span className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60">
        {displayNumber(num)}
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  const columns: ColumnDef<any>[] = [];
  columns.push({
    id: 'serial',
    header: '#',
    size: 60,
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-xs tabular-nums text-muted-foreground">
        {paging.pageIndex * paging.pageSize + row.index + 1}
      </span>
    ),
  });
  if (columnVisibility.name)
    columns.push({
      id: 'name',
      header: 'Name',
      size: 240,
      accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
      cell: ({ row }) => {
        const nm = `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim() || '—';
        const initials = (nm || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w: string) => w[0])
          .join('')
          .toUpperCase() || '—';
        return (
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
              {initials}
            </span>
            <span className="font-medium text-foreground">{nm}</span>
          </div>
        );
      },
    });
  if (columnVisibility.number)
    columns.push({
      accessorKey: 'primary_number',
      header: 'Number',
      size: 160,
      cell: ({ row }) => renderNumberPill(row.original.primary_number),
    });
  if (columnVisibility.street)
    columns.push({
      accessorKey: 'street',
      header: 'Street Address',
      cell: ({ row }) => row.original.street || '-',
    });
  if (columnVisibility.city)
    columns.push({ accessorKey: 'city', header: 'City', cell: ({ row }) => row.original.city || '-' });
  if (columnVisibility.state)
    columns.push({ accessorKey: 'state', header: 'State', cell: ({ row }) => row.original.state || '-' });
  if (columnVisibility.country)
    columns.push({
      accessorKey: 'country',
      header: 'Country',
      cell: ({ row }) => row.original.country || '-',
    });
  if (columnVisibility.company)
    columns.push({
      accessorKey: 'company',
      header: 'Company',
      cell: ({ row }) =>
        row.original.company ? (
          <span className="text-sm text-foreground">{row.original.company}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    });
  if (columnVisibility.contact_groups)
    columns.push({
      id: 'contact_groups',
      header: 'Contact Groups',
      accessorFn: groupNames,
      cell: ({ row }) => <span className="text-sm text-foreground">{groupNames(row.original)}</span>,
    });
  if (customUpload.enabled && columnVisibility.custom_template)
    columns.push({
      id: 'custom_template',
      header: 'Custom Field Template',
      accessorFn: templateName,
      cell: ({ row }) => {
        const name = templateName(row.original);
        return name === '-' ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/20">
            {name}
          </span>
        );
      },
    });
  if (showCustomFields && columnVisibility.additional_details !== false)
    columns.push({
      id: 'additional_details',
      header: 'Customer Additional Details',
      enableSorting: false,
      accessorFn: (row) => additionalDetailsText(row.additional_details),
      cell: ({ row }) => <AdditionalDetailsCell details={row.original.additional_details} contactName={[row.original.first_name, row.original.last_name].filter(Boolean).join(" ")} />,
    });
  if (columnVisibility.dates)
    columns.push({
      id: 'dates',
      header: 'Created / Updated',
      accessorFn: (row) => row.updated_at || row.created_at || '',
      cell: ({ row }) => datesCell(row.original),
    });
  if (columnVisibility.alt1_number)
    columns.push({
      accessorKey: 'alt1_number',
      header: 'Alt. Number 1',
      cell: ({ row }) => renderNumberPill(row.original.alt1_number),
    });
  if (columnVisibility.alt2_number)
    columns.push({
      accessorKey: 'alt2_number',
      header: 'Alt. Number 2',
      cell: ({ row }) => renderNumberPill(row.original.alt2_number),
    });

  columns.push({
    id: 'actions',
    header: 'Actions',
    size: 110,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const nm =
        `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim() ||
        `#${row.original.id}`;
      return (
        <div className="flex items-center gap-1">
          {canEdit && (
            <button
              type="button"
              title={`Edit ${nm}`}
              onClick={() => handleEditClick(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              title={`Delete ${nm}`}
              onClick={() => handleDeleteClick(row.original.id)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    },
  });

  // Stat strip: server total for the current filter (the API caps it at 10,000).
  const filtered = Boolean(search || groupFilter);
  const totalLabel = `${(total || 0).toLocaleString()}${totalCapped ? '+' : ''}`;
  const rowCount = totalCapped
    ? Math.max(total || 0, paging.pageIndex * paging.pageSize + contacts.length + (contacts.length === paging.pageSize ? 1 : 0))
    : total || 0;

  return (
    <FillPage>
      <PageHeader
        title="All Contacts"
        description="Flat list of every contact across your contact groups."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={reload}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            </Button>
            {customUpload.enabled && (
              <Button onClick={() => navigate(`/${ROUTES.CONTACTGROUP.CUSTOM_FIELD_TEMPLATES}`)}>
                <ListPlus className="me-1 h-4 w-4" />
                Custom Field Templates
              </Button>
            )}
            {canCreate && (
              <>
                <Button variant="outline" onClick={() => setShowUploadModal(true)}>
                  <Upload className="me-1 h-4 w-4" />
                  Upload Contacts
                </Button>
                <Button onClick={() => navigate(`/create-all-contacts`)}>
                  <FiPlus className="me-1" />
                  Add Contact
                </Button>
              </>
            )}
          </div>
        }
      />
      {/* Stat strip — total for the current filter */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UsersIcon className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">{filtered ? 'Matching contacts' : 'Total contacts'}</span>
          <span className="font-semibold text-foreground tabular-nums">{totalLabel}</span>
        </div>
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns">
              <FiSliders className="me-1" />
              Columns
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {COLUMN_DEFS.map((c) => (
                <Dropdown.ItemText key={c.key}>
                  <Form.Check
                    type="checkbox"
                    id={`col-toggle-${c.key}`}
                    label={c.label}
                    checked={!!columnVisibility[c.key]}
                    onChange={() => toggleColumn(c.key)}
                  />
                </Dropdown.ItemText>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          {/* Group filter: highlighted with a clear button while a group is applied (it is saved across refresh). */}
          <div
            className={
              'flex h-9 items-center gap-1.5 rounded-md border pl-2.5 pr-1 text-sm ' +
              (groupFilter
                ? 'border-primary/50 bg-primary/10 ring-1 ring-inset ring-primary/20'
                : 'border-input bg-background')
            }
          >
            <span className={'whitespace-nowrap text-xs font-semibold ' + (groupFilter ? 'text-primary' : 'text-muted-foreground')}>
              Group:
            </span>
            <select
              aria-label="Filter by contact group"
              value={groupFilter}
              onChange={(e) => {
                setGroupFilter(e.target.value);
                setPaging((p) => ({ ...p, pageIndex: 0 }));
              }}
              className={
                'h-8 min-w-[150px] cursor-pointer border-0 bg-transparent px-1 text-sm focus:outline-none focus:ring-0 ' +
                (groupFilter ? 'font-semibold text-primary' : 'text-foreground')
              }
            >
              <option value="">All contact groups</option>
              <option value={NO_GROUP}>No group</option>
              {groupList.map((g) => (
                <option key={g.id} value={String(g.id)}>
                  {g.group_name}
                </option>
              ))}
            </select>
            {groupFilter && (
              <button
                type="button"
                title="Clear group filter"
                aria-label="Clear group filter"
                onClick={() => {
                  setGroupFilter('');
                  setPaging((p) => ({ ...p, pageIndex: 0 }));
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-primary transition hover:bg-primary/15"
              >
                ×
              </button>
            )}
          </div>
          {contacts.length > 0 && (
            <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
              Export
              {buttonDisable && (
                <span
                  className="spinner-border spinner-border-sm ml-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
            </Button>
          )}
        </div>

        <DataTable
          tableId="contact-group.all"
          columns={columns}
          data={contacts}
          isLoading={loading}
          leftContent={
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name, number, or company…"
              aria-label="Search contacts"
              maxLength={100}
              className="h-9 w-full max-w-xs"
            />
          }
          emptyTitle={filtered ? 'No matching contacts' : 'No contacts yet'}
          emptyDescription={
            filtered ? 'Try another search or contact group.' : 'Add a contact or import a group to get started.'
          }
          pageSize={paging.pageSize}
          pageSizeOptions={CONTACT_PAGE_SIZES}
          serverPagination={{
            ...paging,
            rowCount,
            onChange: (next) =>
              setPaging(next.pageSize !== paging.pageSize ? { pageIndex: 0, pageSize: next.pageSize } : next),
          }}
          initialColumnPinning={{ right: ['actions'] }}
          disableExport
          enableRowSelection={canDelete}
          getRowId={(row: any) => String(row.id)}
          renderSelectionActions={(ids, clear) => (
            <Button
              size="sm"
              variant="destructive"
              disabled={deleting}
              onClick={() => {
                setBulkDelete({ ids, clear });
                setShowDeleteModal(true);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete ({ids.length})
            </Button>
          )}
        />
      </Card>


      <Modal show={showUploadModal} onHide={() => !uploading && closeUploadModal()} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Contacts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UploadCustomFieldsChooser state={customUpload} disabled={uploading} />
          <p>Download the sample template and upload in same way.</p>
          <Button
            className="mb-4"
            disabled={!customUpload.ready}
            onClick={(event) => {
              const { columns, sample } = customUpload.templateColumns(
                UPLOAD_CONTACT_GROUP_MEMBERS,
                UPLOAD_CONTACT_GROUP_MEMBERS_DATA,
              );
              downloadTemplate(
                event,
                columns,
                customUpload.template ? `Contacts-Upload-${customUpload.template.name}.csv` : 'Contacts-Upload.csv',
                null,
                sample,
              );
            }}
          >
            Download Template
          </Button>
          <Form.Control
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && !/\.csv$/i.test(file.name)) {
                toast.error('Please upload a CSV file.');
                e.target.value = '';
                return setUploadFile(null);
              }
              setUploadFile(file || null);
            }}
          />
          <div className="mt-4 rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="mb-2 font-medium">Upload rules</p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                Contacts uploaded here are not added to any contact group. Add them to a group later
                with Import Contact Members inside that group.
              </li>
              <li>Only .csv files. Keep the template's column headers as they are.</li>
              <li>
                Primary Number must be a valid 10-digit number (+91 / leading 0 is removed). Rows with a
                missing or invalid number are skipped.
              </li>
              <li>
                First Name missing, with control characters (tab, line break), or longer than 255 characters is saved as
                &quot;Unknown&quot;.
              </li>
              <li>
                Name, Street, City, State, Country and Company accept any characters (including special
                characters), up to 255 each. Rows with control characters in these are skipped.
              </li>
              <li>An invalid Alternate Number is ignored; the contact is still added.</li>
              <li>A number repeated in the file is added once. A number already in your contacts is not added again: that contact is updated with the file's details (empty cells keep saved values).</li>
              <li>Skipped rows are listed with their row number after the upload.</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={closeUploadModal} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleSaveUpload} disabled={uploading || !uploadFile || !customUpload.ready}>
            {uploading ? 'Uploading...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => {
          if (deleting) return;
          setShowDeleteModal(false);
          setBulkDelete(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{' '}
          {bulkDelete ? `${bulkDelete.ids.length} selected contact(s)` : 'this contact'}? This
          action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline"
            onClick={() => {
              setShowDeleteModal(false);
              setBulkDelete(null);
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
};

export default AllContacts;
